import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  Plus,
  Search,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Edit,
  Trash2,
  User,
  Home
} from 'lucide-react';

const Ocorrencias = () => {
  const { user } = useAuth();
  const { condominioSelecionado, getCondominioId, isAdministrador } = useCondominio();
  const [ocorrencias, setOcorrencias] = useState([]);
  const [apartamentos, setApartamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingOcorrencia, setEditingOcorrencia] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    tipo: '',
    prioridade: '',
    data_inicio: '',
    data_fim: '',
    apartamento_id: ''
  });
  const [formData, setFormData] = useState({
    ocorrencia_titulo: '',
    ocorrencia_descricao: '',
    ocorrencia_tipo: 'manutencao',
    ocorrencia_prioridade: 'media',
    apartamento_id: '',
    ocorrencia_data_ocorrencia: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    const condominioId = getCondominioId();
    if (condominioId) {
      loadOcorrencias();
      loadApartamentos();
    }
  }, [condominioSelecionado]);

  const loadOcorrencias = async () => {
    try {
      setLoading(true);
      const condominioId = getCondominioId();
      if (!condominioId) return;

      const params = new URLSearchParams();
      params.append('condominio_id', condominioId);
      
      if (filters.status) params.append('status', filters.status);
      if (filters.tipo) params.append('tipo', filters.tipo);
      if (filters.prioridade) params.append('prioridade', filters.prioridade);
      if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
      if (filters.data_fim) params.append('data_fim', filters.data_fim);
      if (filters.apartamento_id) params.append('apartamento_id', filters.apartamento_id);

      const response = await api.get(`/ocorrencias?${params.toString()}`);
      setOcorrencias(response.data.data.ocorrencias);
    } catch (error) {
      console.error('Erro ao carregar ocorrências:', error);
      setErrorMessage('Erro ao carregar ocorrências');
    } finally {
      setLoading(false);
    }
  };

  const loadApartamentos = async () => {
    try {
      const condominioId = getCondominioId();
      if (!condominioId) return;

      const response = await api.get(`/apartamentos?condominio_id=${condominioId}`);
      setApartamentos(response.data.data.apartamentos);
    } catch (error) {
      console.error('Erro ao carregar apartamentos:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const condominioId = getCondominioId();
      const dadosParaEnvio = {
        ...formData,
        condominio_id: condominioId
      };

      if (editingOcorrencia) {
        await api.put(`/ocorrencias/${editingOcorrencia.ocorrencia_id}`, dadosParaEnvio);
      } else {
        await api.post('/ocorrencias', dadosParaEnvio);
      }

      setShowModal(false);
      resetForm();
      loadOcorrencias();
    } catch (error) {
      console.error('Erro ao salvar ocorrência:', error);
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        setErrorMessage(errorMessages);
      } else {
        setErrorMessage('Erro ao salvar ocorrência');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (ocorrencia) => {
    setEditingOcorrencia(ocorrencia);
    setFormData({
      ocorrencia_titulo: ocorrencia.ocorrencia_titulo,
      ocorrencia_descricao: ocorrencia.ocorrencia_descricao,
      ocorrencia_tipo: ocorrencia.ocorrencia_tipo,
      ocorrencia_prioridade: ocorrencia.ocorrencia_prioridade,
      apartamento_id: ocorrencia.apartamento_id || '',
      ocorrencia_data_ocorrencia: ocorrencia.ocorrencia_data_ocorrencia ? 
        new Date(ocorrencia.ocorrencia_data_ocorrencia).toISOString().split('T')[0] : 
        new Date().toISOString().split('T')[0]
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta ocorrência?')) {
      return;
    }

    try {
      await api.delete(`/ocorrencias/${id}`);
      loadOcorrencias();
    } catch (error) {
      console.error('Erro ao excluir ocorrência:', error);
      setErrorMessage('Erro ao excluir ocorrência');
    }
  };

  const resetForm = () => {
    setFormData({
      ocorrencia_titulo: '',
      ocorrencia_descricao: '',
      ocorrencia_tipo: 'manutencao',
      ocorrencia_prioridade: 'media',
      apartamento_id: '',
      ocorrencia_data_ocorrencia: new Date().toISOString().split('T')[0]
    });
    setEditingOcorrencia(null);
    setErrorMessage('');
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'aberta': { color: 'bg-red-100 text-red-800', text: 'Aberta' },
      'em_andamento': { color: 'bg-orange-100 text-orange-800', text: 'Em Andamento' },
      'resolvida': { color: 'bg-green-100 text-green-800', text: 'Resolvida' },
      'cancelada': { color: 'bg-gray-100 text-gray-800', text: 'Cancelada' }
    };
    const statusInfo = statusMap[status] || { color: 'bg-gray-100 text-gray-800', text: 'Desconhecido' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>{statusInfo.text}</span>;
  };

  const getPrioridadeBadge = (prioridade) => {
    const prioridadeMap = {
      'baixa': { color: 'bg-blue-100 text-blue-800', text: 'Baixa' },
      'media': { color: 'bg-yellow-100 text-yellow-800', text: 'Média' },
      'alta': { color: 'bg-orange-100 text-orange-800', text: 'Alta' },
      'urgente': { color: 'bg-red-100 text-red-800', text: 'Urgente' }
    };
    const prioridadeInfo = prioridadeMap[prioridade] || { color: 'bg-gray-100 text-gray-800', text: 'Média' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${prioridadeInfo.color}`}>{prioridadeInfo.text}</span>;
  };

  const getTipoText = (tipo) => {
    const tipoMap = {
      'manutencao': 'Manutenção',
      'seguranca': 'Segurança',
      'limpeza': 'Limpeza',
      'ruido': 'Ruído',
      'vazamento': 'Vazamento',
      'eletrico': 'Elétrico',
      'outros': 'Outros'
    };
    return tipoMap[tipo] || tipo;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <AlertTriangle className="w-6 h-6 mr-2 text-orange-500" />
          Ocorrências
        </h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="btn-primary flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Ocorrência
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="input"
            >
              <option value="">Todos</option>
              <option value="aberta">Aberta</option>
              <option value="em_andamento">Em Andamento</option>
              <option value="resolvida">Resolvida</option>
              <option value="cancelada">Cancelada</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select
              value={filters.tipo}
              onChange={(e) => setFilters(prev => ({ ...prev, tipo: e.target.value }))}
              className="input"
            >
              <option value="">Todos</option>
              <option value="manutencao">Manutenção</option>
              <option value="seguranca">Segurança</option>
              <option value="limpeza">Limpeza</option>
              <option value="ruido">Ruído</option>
              <option value="vazamento">Vazamento</option>
              <option value="eletrico">Elétrico</option>
              <option value="outros">Outros</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prioridade</label>
            <select
              value={filters.prioridade}
              onChange={(e) => setFilters(prev => ({ ...prev, prioridade: e.target.value }))}
              className="input"
            >
              <option value="">Todas</option>
              <option value="baixa">Baixa</option>
              <option value="media">Média</option>
              <option value="alta">Alta</option>
              <option value="urgente">Urgente</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Apartamento</label>
            <select
              value={filters.apartamento_id}
              onChange={(e) => setFilters(prev => ({ ...prev, apartamento_id: e.target.value }))}
              className="input"
            >
              <option value="">Todos</option>
              {apartamentos.map(apt => (
                <option key={apt.apartamento_id} value={apt.apartamento_id}>
                  {apt.apartamento_bloco} - {apt.apartamento_numero}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Início</label>
            <input
              type="date"
              value={filters.data_inicio}
              onChange={(e) => setFilters(prev => ({ ...prev, data_inicio: e.target.value }))}
              className="input"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={loadOcorrencias}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2"
            >
              <Search className="w-4 h-4" />
              Filtrar
            </button>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => setFilters({
              status: '',
              tipo: '',
              prioridade: '',
              data_inicio: '',
              data_fim: '',
              apartamento_id: ''
            })}
            className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 text-sm"
          >
            Limpar Filtros
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Título
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prioridade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Apartamento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ocorrencias.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    Nenhuma ocorrência encontrada
                  </td>
                </tr>
              ) : (
                ocorrencias.map((ocorrencia) => (
                  <tr key={ocorrencia.ocorrencia_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {ocorrencia.ocorrencia_titulo}
                      </div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {ocorrencia.ocorrencia_descricao}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getTipoText(ocorrencia.ocorrencia_tipo)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPrioridadeBadge(ocorrencia.ocorrencia_prioridade)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(ocorrencia.ocorrencia_status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {ocorrencia.apartamento ? 
                        `${ocorrencia.apartamento.apartamento_bloco} - ${ocorrencia.apartamento.apartamento_numero}` : 
                        'Geral'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(ocorrencia.ocorrencia_data_ocorrencia).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(ocorrencia)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {(isAdministrador || user?.tipo === 'sindico' || user?.tipo === 'subsindico') && (
                          <button
                            onClick={() => handleDelete(ocorrencia.ocorrencia_id)}
                            className="text-red-600 hover:text-red-900"
                            title="Excluir"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto ocorrencias-modal">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingOcorrencia ? 'Editar Ocorrência' : 'Nova Ocorrência'}
                </h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingOcorrencia(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              {errorMessage && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {errorMessage}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Título *
                  </label>
                  <input
                    type="text"
                    name="ocorrencia_titulo"
                    value={formData.ocorrencia_titulo}
                    onChange={handleInputChange}
                    className="input"
                    required
                    maxLength={100}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição *
                  </label>
                  <textarea
                    name="ocorrencia_descricao"
                    value={formData.ocorrencia_descricao}
                    onChange={handleInputChange}
                    className="input"
                    rows={4}
                    required
                    maxLength={1000}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo *
                    </label>
                    <select
                      name="ocorrencia_tipo"
                      value={formData.ocorrencia_tipo}
                      onChange={handleInputChange}
                      className="input"
                      required
                    >
                      <option value="manutencao">Manutenção</option>
                      <option value="seguranca">Segurança</option>
                      <option value="limpeza">Limpeza</option>
                      <option value="ruido">Ruído</option>
                      <option value="vazamento">Vazamento</option>
                      <option value="eletrico">Elétrico</option>
                      <option value="outros">Outros</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prioridade *
                    </label>
                    <select
                      name="ocorrencia_prioridade"
                      value={formData.ocorrencia_prioridade}
                      onChange={handleInputChange}
                      className="input"
                      required
                    >
                      <option value="baixa">Baixa</option>
                      <option value="media">Média</option>
                      <option value="alta">Alta</option>
                      <option value="urgente">Urgente</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Apartamento
                    </label>
                    <select
                      name="apartamento_id"
                      value={formData.apartamento_id}
                      onChange={handleInputChange}
                      className="input"
                    >
                      <option value="">Geral</option>
                      {apartamentos.map(apt => (
                        <option key={apt.apartamento_id} value={apt.apartamento_id}>
                          {apt.apartamento_bloco} - {apt.apartamento_numero}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data da Ocorrência *
                    </label>
                    <input
                      type="date"
                      name="ocorrencia_data_ocorrencia"
                      value={formData.ocorrencia_data_ocorrencia}
                      onChange={handleInputChange}
                      className="input"
                      required
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary"
                  >
                    {isSubmitting ? 'Salvando...' : (editingOcorrencia ? 'Atualizar' : 'Salvar')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ocorrencias;

// Estilos CSS personalizados para o modal de Ocorrências
const ocorrenciasStyles = `
  .ocorrencias-modal .input-field {
    width: 100%;
    padding: 0.75rem;
    border: 2px solid #e5e7eb;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    transition: all 0.2s ease-in-out;
    background-color: #f9fafb;
  }
  
  .ocorrencias-modal .input-field:focus {
    outline: none;
    border-color: #3b82f6;
    background-color: #ffffff;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  .ocorrencias-modal .input-field:hover {
    border-color: #d1d5db;
    background-color: #ffffff;
  }
  
  .ocorrencias-modal .btn-primary {
    background-color: #3b82f6;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    border: none;
    font-weight: 500;
    transition: all 0.2s ease-in-out;
  }
  
  .ocorrencias-modal .btn-primary:hover:not(:disabled) {
    background-color: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  .ocorrencias-modal .btn-primary:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
  }
  
  .ocorrencias-modal .btn-secondary {
    background-color: #6b7280;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    border: none;
    font-weight: 500;
    transition: all 0.2s ease-in-out;
  }
  
  .ocorrencias-modal .btn-secondary:hover {
    background-color: #4b5563;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(107, 114, 128, 0.3);
  }
`;

// Adicionar estilos ao documento
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = ocorrenciasStyles;
  document.head.appendChild(styleSheet);
}